import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Mail, MessagesSquare, Paperclip } from 'lucide-react';
import EmailAttachments from './EmailAttachments';
import {
  buildEmailThreads,
  getEmailPreview,
  getInitials,
  getReadableEmailBody,
  isFromCounterpart,
  messageMatchesDirection,
  parseEmailAddress,
  parseEmailAddressList,
} from '../../utils/emailThread';

const formatDate = (date) =>
  date
    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

const formatTime = (date) =>
  date ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '';

const Avatar = ({ person, size = 'md' }) => {
  const dimensions = size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-9 h-9 text-xs';
  const label = person?.name || person?.email || '';

  if (person?.profileImage) {
    return (
      <img
        src={person.profileImage}
        alt={label}
        className={`${dimensions} shrink-0 rounded-full object-cover bg-gray-200 dark:bg-gray-700`}
      />
    );
  }

  return (
    <div
      className={`${dimensions} shrink-0 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 font-semibold flex items-center justify-center`}
      title={label}
    >
      {person?.initials || getInitials(label)}
    </div>
  );
};

const DirectionBadge = ({ inbound, labels }) => (
  <span
    className={`px-2 py-0.5 text-[11px] font-medium rounded-full whitespace-nowrap ${
      inbound
        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200'
        : 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200'
    }`}
  >
    {inbound ? labels.from : labels.to}
  </span>
);

const MessageRow = ({ message, counterpartEmail, labels, defaultOpen, resolvePerson }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [showQuoted, setShowQuoted] = useState(false);

  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  const sender = resolvePerson(message.from);
  const toList = parseEmailAddressList(message.to);
  const ccList = parseEmailAddressList(message.cc);
  const inbound = isFromCounterpart(message, counterpartEmail);
  const { body, quoted } = useMemo(() => getReadableEmailBody(message), [message]);
  const preview = useMemo(() => getEmailPreview(message), [message]);
  const fetchedBy = message.fetchedByAll?.length ? message.fetchedByAll : message.fetchedBy ? [message.fetchedBy] : [];

  return (
    <div className="px-3 py-3 sm:px-4 min-w-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full min-w-0 flex items-start gap-3 text-left"
      >
        <Avatar person={sender} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate min-w-0 max-w-full">
              {sender.name || sender.email || 'Unknown sender'}
            </span>
            <DirectionBadge inbound={inbound} labels={labels} />
            {message.isRead === false && (
              <span className="w-2 h-2 rounded-full bg-blue-500" title="Unread" aria-label="Unread" />
            )}
          </div>
          {open ? (
            <div className="mt-0.5 space-y-0.5 text-xs text-gray-500 dark:text-gray-400 min-w-0">
              {sender.email && <div className="truncate">{sender.email}</div>}
              {toList.length > 0 && (
                <div className="truncate">
                  <span className="font-medium">To:</span> {toList.map((entry) => entry.name || entry.email).join(', ')}
                </div>
              )}
              {ccList.length > 0 && (
                <div className="truncate">
                  <span className="font-medium">Cc:</span> {ccList.map((entry) => entry.name || entry.email).join(', ')}
                </div>
              )}
            </div>
          ) : (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{preview || 'No preview available'}</p>
          )}
        </div>
        <div className="text-right shrink-0 pl-1 whitespace-nowrap">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{formatDate(message.dateValue)}</div>
          <div className="text-[11px] text-gray-400 dark:text-gray-500">{formatTime(message.dateValue)}</div>
        </div>
        <ChevronRight
          className={`w-4 h-4 mt-0.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div className="mt-3 sm:pl-10 min-w-0">
          <div className="text-sm leading-relaxed text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
            {body || 'No message content available.'}
          </div>

          {quoted && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowQuoted((value) => !value)}
                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
              </button>
              {showQuoted && (
                <div className="mt-2 border-l-2 border-gray-300 dark:border-gray-600 pl-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                  {quoted}
                </div>
              )}
            </div>
          )}

          <EmailAttachments message={message} />

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {fetchedBy.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                {fetchedBy[0].profileImage ? (
                  <img
                    src={fetchedBy[0].profileImage}
                    alt={fetchedBy[0].name}
                    className="w-5 h-5 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-gray-400 dark:bg-gray-600 flex items-center justify-center text-white text-[10px] font-medium">
                    {fetchedBy[0].initials || getInitials(fetchedBy[0].name)}
                  </div>
                )}
                <span>
                  From {fetchedBy.map((user) => user.name).filter(Boolean).join(', ')}
                  {fetchedBy.length === 1 ? "'s inbox" : ' inboxes'}
                </span>
              </div>
            )}
            {message.htmlLink && (
              <a
                href={message.htmlLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                View in Gmail
                <ExternalLink className="w-3 h-3" aria-hidden />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ThreadCard = ({ thread, counterpartEmail, labels, defaultExpanded, resolvePerson }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const latest = thread.latest;
  const latestSender = resolvePerson(latest?.from);
  const inbound = isFromCounterpart(latest, counterpartEmail);
  const preview = useMemo(() => getEmailPreview(latest), [latest]);
  const participantNames = thread.participants.map((person) => person.name || person.email);
  const hasAttachments = thread.messages.some((message) =>
    (message.attachments || []).some((attachment) => !attachment.inline)
  );

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="w-full min-w-0 flex items-start gap-3 px-3 py-3 sm:px-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
      >
        <Avatar person={latestSender} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate min-w-0 max-w-full">
              {thread.subject}
            </h3>
            {thread.messageCount > 1 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                <MessagesSquare className="w-3 h-3" aria-hidden />
                {thread.messageCount}
              </span>
            )}
            <DirectionBadge inbound={inbound} labels={labels} />
            {hasAttachments && (
              <Paperclip className="w-3.5 h-3.5 text-gray-400" aria-label="Has attachments" />
            )}
            {thread.unreadCount > 0 && (
              <span className="w-2 h-2 rounded-full bg-blue-500" title={`${thread.unreadCount} unread`} />
            )}
          </div>
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">
            {participantNames.join(', ')}
          </div>
          {!expanded && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
              {preview || 'No preview available'}
            </p>
          )}
        </div>
        <div className="text-right shrink-0 pl-1 whitespace-nowrap">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{formatDate(thread.latestDate)}</div>
          <div className="text-[11px] text-gray-400 dark:text-gray-500">{formatTime(thread.latestDate)}</div>
        </div>
        <ChevronDown
          className={`w-4 h-4 mt-0.5 shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700 bg-gray-50/60 dark:bg-gray-800/60">
          {thread.messages.map((message, index) => (
            <MessageRow
              key={message.id || `${thread.id}-${index}`}
              message={message}
              counterpartEmail={counterpartEmail}
              labels={labels}
              defaultOpen={index === thread.messages.length - 1}
              resolvePerson={resolvePerson}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Threaded, readable email list shared by the Lead and Client overview pages.
 * Handles duplicate copies of the same message pulled from several internal
 * mailboxes, decodes Gmail's HTML entities, and hides quoted reply chains.
 */
const EmailThreadList = ({
  emails = [],
  counterpartEmail = '',
  counterpartPerson = null,
  directory = [],
  filter = 'all',
  labels = { from: 'From Lead', to: 'To Lead' },
  emptyMessage = 'No emails found.',
  threadsPerPage = 5,
}) => {
  const [page, setPage] = useState(1);

  const threads = useMemo(() => {
    const filtered = emails.filter((email) => messageMatchesDirection(email, counterpartEmail, filter));
    return buildEmailThreads(filtered);
  }, [emails, counterpartEmail, filter]);

  // Photos come from the internal-user directory, whoever fetched the message, and
  // the lead/client contact themselves — keyed by email address.
  const photoDirectory = useMemo(() => {
    const map = new Map();
    const add = (person) => {
      const key = String(person?.email || '').toLowerCase().trim();
      if (!key) return;
      const existing = map.get(key);
      map.set(key, {
        email: key,
        name: person.name || existing?.name || '',
        initials: person.initials || existing?.initials || '',
        profileImage: person.profileImage || existing?.profileImage || null,
      });
    };

    directory.forEach(add);
    emails.forEach((email) => {
      const users = email?.fetchedByAll?.length ? email.fetchedByAll : email?.fetchedBy ? [email.fetchedBy] : [];
      users.forEach(add);
    });
    if (counterpartPerson) add({ ...counterpartPerson, email: counterpartPerson.email || counterpartEmail });

    return map;
  }, [directory, emails, counterpartPerson, counterpartEmail]);

  const resolvePerson = useMemo(
    () => (raw) => {
      const parsed = parseEmailAddress(raw);
      const known = photoDirectory.get(parsed.email);
      if (!known) return parsed;
      return {
        ...parsed,
        name: parsed.name || known.name,
        initials: known.initials,
        profileImage: known.profileImage,
      };
    },
    [photoDirectory]
  );

  const totalPages = Math.max(1, Math.ceil(threads.length / threadsPerPage));

  useEffect(() => {
    setPage(1);
  }, [filter, counterpartEmail, emails]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  if (!threads.length) {
    return (
      <div className="py-10 text-center text-gray-500 dark:text-gray-400">
        <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" aria-hidden />
        {emptyMessage}
      </div>
    );
  }

  const startIndex = (page - 1) * threadsPerPage;
  const visibleThreads = threads.slice(startIndex, startIndex + threadsPerPage);

  const totalMessages = threads.reduce((total, thread) => total + thread.messageCount, 0);

  return (
    <div className="min-w-0 w-full">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 text-center">
        {threads.length} conversation{threads.length === 1 ? '' : 's'} · {totalMessages} message
        {totalMessages === 1 ? '' : 's'}
      </div>

      <div className="space-y-3 min-w-0">
        {visibleThreads.map((thread, index) => (
          <ThreadCard
            key={thread.id}
            thread={thread}
            counterpartEmail={counterpartEmail}
            labels={labels}
            defaultExpanded={page === 1 && index === 0}
            resolvePerson={resolvePerson}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page === 1}
            className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex items-center space-x-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= page - 1 && pageNumber <= page + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => setPage(pageNumber)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                      page === pageNumber
                        ? 'bg-red-600 text-white'
                        : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              }
              if (pageNumber === page - 2 || pageNumber === page + 2) {
                return (
                  <span key={pageNumber} className="px-2 text-gray-500 dark:text-gray-400">
                    ...
                  </span>
                );
              }
              return null;
            })}
          </div>

          <button
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailThreadList;
